import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { bookingPagesAPI, eventTypesAPI } from "../services/schedulerAPI";
import { Link } from "react-router-dom";
import BookingLinkSelector from "../components/BookingLinkSelector";

const BookingPages = () => {
  const [bookingPages, setBookingPages] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  // const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pagesData, eventTypesData] = await Promise.all([
        bookingPagesAPI.list(),
        eventTypesAPI.list(),
        // bookingsAPI.list(), // Not used in Zcal-style UI for now
      ]);

      setBookingPages(pagesData);
      setEventTypes(eventTypesData);
      // setBookings(bookingsData);
    } catch (error) {
      console.error("Failed to load booking pages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePage = async (pageData) => {
    try {
      if (currentPage?.id) {
        await bookingPagesAPI.update(currentPage.id, pageData);
        showSuccessMsg("Booking page updated successfully!");
      } else {
        await bookingPagesAPI.create(pageData);
        showSuccessMsg("Booking page created successfully!");
      }
      await loadData();
      setShowModal(false);
      setCurrentPage(null);
    } catch (error) {
      console.error("Failed to save booking page:", error);
      alert(error.response?.data?.detail || "Failed to save booking page. Please try again.");
    }
  };

  // const handleDeletePage = async (pageId) => {
  //   if (!window.confirm("Delete this booking page? This cannot be undone."))
  //     return;

  //   try {
  //     await bookingPagesAPI.delete(pageId);
  //     await loadData();
  //     showSuccessMsg("Booking page deleted successfully!");
  //   } catch (error) {
  //     console.error("Failed to delete booking page:", error);
  //   }
  // };

  const handleCopyLink = (slug) => {
    const link = `${window.location.origin}/book/${slug}`;
    navigator.clipboard.writeText(link);
    showSuccessMsg("Booking link copied to clipboard!");
  };

  const [expandedPage, setExpandedPage] = useState(null);

  const toggleExpandPage = (pageId) => {
    setExpandedPage(expandedPage === pageId ? null : pageId);
  };

  const showSuccessMsg = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // const getBookingsForPage = (pageId) => {
  //   return bookings.filter((b) => b.booking_page_id === pageId).length;
  // };

  // const getEventTypeName = (eventTypeId) => {
  //   const eventType = eventTypes.find((et) => et.id === eventTypeId);
  //   return eventType?.name || "Unknown";
  // };

  // Booking Page Modal
  const BookingPageModal = () => {
    const [formData, setFormData] = useState(
      currentPage || {
        slug: "",
        title: "",
        description: "",
        event_type_id: eventTypes[0]?.id || "",
        custom_questions: [],
        redirect_url: "",
        is_active: true,
      }
    );

    const [newQuestion, setNewQuestion] = useState({
      id: "",
      label: "",
      type: "text",
      required: false,
    });

    const handleAddQuestion = () => {
      if (!newQuestion.label) {
        alert("Please enter a question label");
        return;
      }

      const question = {
        ...newQuestion,
        id: newQuestion.label.toLowerCase().replace(/\s+/g, "_"),
      };

      setFormData({
        ...formData,
        custom_questions: [...(formData.custom_questions || []), question],
      });

      setNewQuestion({ id: "", label: "", type: "text", required: false });
    };

    const handleRemoveQuestion = (questionId) => {
      setFormData({
        ...formData,
        custom_questions: formData.custom_questions.filter(
          (q) => q.id !== questionId
        ),
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="apple-modal apple-glass-strong max-w-3xl w-full my-8"
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">
              {currentPage ? "Edit Booking Page" : "Create Booking Page"}
            </h2>
            <p className="text-gray-600 mt-1">
              Set up a shareable link for guests to book time with you
            </p>
          </div>

          <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* URL Slug */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Booking Page URL
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">
                  {window.location.origin}/book/
                </span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                    })
                  }
                  placeholder="your-name"
                  className="apple-input flex-1"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Use lowercase letters, numbers, and hyphens only
              </p>
            </div>

            {/* Page Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Page Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g., Book time with John"
                className="apple-input w-full"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Welcome Message
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Tell guests what this booking is for..."
                rows={3}
                className="apple-input w-full"
              />
            </div>

            {/* Event Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Event Type
              </label>
              <select
                value={formData.event_type_id}
                onChange={(e) =>
                  setFormData({ ...formData, event_type_id: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {eventTypes.map((et) => (
                  <option key={et.id} value={et.id}>
                    {et.name} ({et.duration_minutes} min)
                  </option>
                ))}
              </select>
              {eventTypes.length === 0 && (
                <p className="text-sm text-red-600 mt-1">
                  Please create an event type first in Calendar Settings
                </p>
              )}
            </div>

            {/* Custom Questions */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Custom Questions
              </label>

              {/* Existing Questions */}
              {formData.custom_questions?.length > 0 && (
                <div className="space-y-2 mb-3">
                  {formData.custom_questions.map((question) => (
                    <div
                      key={question.id}
                      className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg"
                    >
                      <div>
                        <span className="font-medium text-gray-900">
                          {question.label}
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          ({question.type})
                        </span>
                        {question.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveQuestion(question.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Question */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={newQuestion.label}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, label: e.target.value })
                    }
                    placeholder="Question label"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <select
                    value={newQuestion.type}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, type: e.target.value })
                    }
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="number">Number</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={newQuestion.required}
                      onChange={(e) =>
                        setNewQuestion({
                          ...newQuestion,
                          required: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    Required
                  </label>
                  <button
                    onClick={handleAddQuestion}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm font-medium"
                  >
                    Add Question
                  </button>
                </div>
              </div>
            </div>

            {/* Redirect URL */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Redirect URL (Optional)
              </label>
              <input
                type="url"
                value={formData.redirect_url}
                onChange={(e) =>
                  setFormData({ ...formData, redirect_url: e.target.value })
                }
                placeholder="https://yoursite.com/thank-you"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Redirect guests to this URL after booking
              </p>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Active (publicly accessible)
              </label>
            </div>
          </div>

          <div className="p-6 border-t apple-divider flex gap-4">
            <button
              onClick={() => {
                setShowModal(false);
                setCurrentPage(null);
              }}
              className="flex-1 apple-btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSavePage(formData)}
              disabled={!formData.slug || !formData.title || !formData.event_type_id}
              className="flex-1 apple-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentPage ? "Update Page" : "Create Page"}
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Nikesh Masiwal
            </h1>
            <a
              href={`${window.location.origin}/book/nikesh`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
            >
              {window.location.origin.replace('http://', '').replace('https://', '')}/book/nikesh
              <span>↗</span>
            </a>
          </div>
          <Link
            to="/calendar/select-link-type"
            className="apple-btn-primary apple-hover-lift flex items-center gap-2"
          >
            <span className="text-xl">+</span>
            New Link
          </Link>
        </div>

        {/* Success Message */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-xl flex items-center gap-3"
            >
              <span className="text-2xl">✓</span>
              <span className="font-semibold">{successMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading booking pages...</p>
          </div>
        ) : bookingPages.length === 0 ? (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="apple-glass-strong border-2 border-dashed border-gray-300 p-12 text-center apple-scale-in"
          >
            <div className="text-6xl mb-4">📅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No Booking Pages Yet
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Create your first booking page to start accepting appointments.
              Share the link with your clients and let them schedule time with
              you.
            </p>
            <button
              onClick={() => {
                setCurrentPage(null);
                setShowModal(true);
              }}
              className="apple-btn-primary apple-hover-lift text-lg"
              style={{ padding: '16px 32px' }}
            >
              Create Your First Booking Page
            </button>
          </motion.div>
        ) : (
          /* My Invites Section */
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                My invites
                <span className="text-gray-400">ⓘ</span>
              </h2>
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                View all invites
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bookingPages.map((page) => {
                const eventType = eventTypes.find(et => et.id === page.event_type_id);
                return (
                  <motion.div
                    key={page.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -4 }}
                    className="apple-card p-6 apple-hover-lift overflow-hidden apple-fade-in"
                  >
                    {/* Title */}
                    <div className="flex items-start gap-3 mb-4">
                      <div className="text-blue-600 text-2xl">👤</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {page.title}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <span>🕐</span>
                            <span>{eventType?.duration_minutes || 30} min</span>
                          </div>
                          {page.location_type === 'google_meet' && (
                            <div className="flex items-center gap-1">
                              <span>📍</span>
                              <span>Google Meet</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between gap-2 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => toggleExpandPage(page.id)}
                        className="flex-1 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors font-medium text-sm"
                      >
                        {expandedPage === page.id ? "Hide Links" : "Share Links"}
                      </button>
                      <button
                        onClick={() => {
                          setCurrentPage(page);
                          setShowModal(true);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="More options"
                      >
                        ⋯
                      </button>
                    </div>

                    {/* Expanded Link Selector */}
                    <AnimatePresence>
                      {expandedPage === page.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden mt-4"
                        >
                          <BookingLinkSelector bookingPageSlug={page.slug} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            {/* Upcoming Events Section */}
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  Upcoming AgentGarden events
                  <span className="text-gray-400">ⓘ</span>
                </h2>
                <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  View all events
                </button>
              </div>
              <div className="apple-card p-12 text-center">
                <p className="text-gray-600">
                  You don't have any upcoming AgentGarden events. Share your scheduling links with guests. As events are scheduled, they'll appear here.
                </p>
              </div>
            </div>

            {/* Payment CTA */}
            <div className="mt-8 apple-glass p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-xl">💰</span>
              </div>
              <div className="flex-1">
                <button className="text-blue-600 hover:text-blue-700 font-medium">
                  Get paid for client meetings.
                </button>
                <span className="text-gray-600"> Connect your account to Stripe to start accepting payments on AgentGarden.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Booking Page Modal */}
      {showModal && <BookingPageModal />}
    </div>
  );
};

export default BookingPages;
